import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import QuizContent from '../shared/QuizContent';
import QuizTitle from '../shared/QuizTitle';
import QuizOptions from '../shared/QuizOptions';
import QuizOption from '../shared/QuizOption';
import LazyImage from '../shared/LazyImage';
import { getQuizOptionsData } from '../../constants';
import { useVideoAnimation } from '../../hooks/useVideoAnimation';
import {
  ethnicityKey,
  useGrowthBookValues,
} from '../../contexts/GrowthBookContext';

const Step4 = ({ onAnswer, onPrevious, selections, isTransitioning }) => {
  const { t } = useTranslation();
  const { getVideoProps } = useVideoAnimation();
  const { videos: gbVideos } = useGrowthBookValues();

  const hairStyles = getQuizOptionsData('hairStyle');
  const eyeColors = getQuizOptionsData('eyeColor');

  // Step 4 picks up the same Step 1 ethnicity branch as Step 2/3. Both
  // haircut and eye videos are optional — empty URL → fall back to the
  // option's static image as before.
  const eKey = ethnicityKey(selections?.step1?.ethnicity);
  const resolveHairVideo = (style) =>
    (gbVideos?.step4?.[eKey]?.haircut?.[style.value] || '').trim();
  const resolveEyeVideo = (color) =>
    (gbVideos?.step4?.[eKey]?.eye?.[color.value] || '').trim();

  const [selectedHairStyle, setSelectedHairStyle] = useState(
    selections?.step4?.hairStyle || hairStyles[0]?.value || '',
  );
  const [selectedEyeColor, setSelectedEyeColor] = useState(
    selections?.step4?.eyeColor || eyeColors[0]?.value || '',
  );

  const handleHairStyleClick = useCallback(
    (value) => {
      if (isTransitioning) return;
      setSelectedHairStyle(value);
    },
    [isTransitioning],
  );

  const handleEyeColorClick = useCallback(
    (value) => {
      if (isTransitioning) return;
      setSelectedEyeColor(value);
    },
    [isTransitioning],
  );

  const handleContinue = useCallback(() => {
    if (isTransitioning || !selectedHairStyle || !selectedEyeColor) return;
    onAnswer('step4', {
      hairStyle: selectedHairStyle,
      eyeColor: selectedEyeColor,
    });
  }, [selectedHairStyle, selectedEyeColor, onAnswer, isTransitioning]);

  const handlePrevious = useCallback(() => {
    if (isTransitioning) return;
    onPrevious();
  }, [onPrevious, isTransitioning]);

  const canContinue = selectedHairStyle && selectedEyeColor;

  // Inline-style block reused for both haircut and eye videos so the video
  // element fills the same tile space the image previously occupied.
  const videoFillStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  return (
    <div className={`quizContentWrapper ${isTransitioning ? 'no-click' : ''}`}>
      <QuizContent className="step3-design">
        <div className="step3-block">
          <QuizTitle>
            {t('choose')} <span>{t('hairS_text')}</span>
          </QuizTitle>
          <QuizOptions className="hairstyle-options">
            {hairStyles.map((style) => {
              const videoSrc = resolveHairVideo(style);
              return (
                <QuizOption
                  key={style.value}
                  value={style.value}
                  name="hairStyle"
                  onClick={() => handleHairStyleClick(style.value)}
                  className={`hairstyle-option ${
                    selectedHairStyle === style.value
                      ? 'selected'
                      : selectedHairStyle
                      ? 'not-selected'
                      : ''
                  } ${hairStyles.length % 2 === 0 ? 'even' : 'odd'}`}
                >
                  <div className="hairstyle-image">
                    {videoSrc ? (
                      <video
                        src={videoSrc}
                        {...getVideoProps()}
                        controls={false}
                        playsInline
                        webkit-playsinline=""
                        style={videoFillStyle}
                      />
                    ) : (
                      <LazyImage src={style.image} alt={style.alt} />
                    )}
                  </div>
                  <div className="image-title">{t(style.text)}</div>
                </QuizOption>
              );
            })}
          </QuizOptions>
        </div>

        <div className="step3-block">
          <QuizTitle>
            {t('choose')} <span>{t('eye_text')}</span>
          </QuizTitle>
          <QuizOptions className="eye-color-options">
            {eyeColors.map((color) => {
              const videoSrc = resolveEyeVideo(color);
              return (
                <QuizOption
                  key={color.value}
                  value={color.value}
                  name="eyeColor"
                  onClick={() => handleEyeColorClick(color.value)}
                  className={`eye-color-option ${
                    selectedEyeColor === color.value
                      ? 'selected'
                      : selectedEyeColor
                      ? 'not-selected'
                      : ''
                  } ${eyeColors.length % 2 === 0 ? 'even' : 'odd'}`}
                >
                  <div className="eye-color-image">
                    {videoSrc ? (
                      // Eye tiles intentionally do NOT autoplay — we want a
                      // static first-frame "photo" feel. `#t=0.001` nudges
                      // Safari/iOS into rendering the first frame instead of
                      // an empty black box when the video isn't playing.
                      <video
                        src={`${videoSrc}#t=0.001`}
                        muted
                        playsInline
                        webkit-playsinline=""
                        preload="auto"
                        controls={false}
                        style={videoFillStyle}
                      />
                    ) : (
                      <LazyImage src={color.image} alt={color.alt} />
                    )}
                  </div>
                  <div className="image-title">{t(color.text)}</div>
                </QuizOption>
              );
            })}
          </QuizOptions>
        </div>

        <div className="navigation-wrapper">
          <button
            className="previous-button"
            onClick={handlePrevious}
            disabled={isTransitioning}
          >
            {t('previous')}
          </button>
          <button
            className="continue-button"
            onClick={handleContinue}
            disabled={!canContinue || isTransitioning}
          >
            {t('next')}
          </button>
        </div>
      </QuizContent>
    </div>
  );
};

export default Step4;
